import { AccessTokenPayload, AuthCodePayload, AuthLibSettings } from './interfaces';
import { getAuthCode } from './auth-iframe';
import { getCredentials, getDeviceName, getRedirectUri, getState, getUserAgent } from './util';

export const PKCE_METHOD = 'S256';

let requestCount = 0;

/**
 * Takes an auth code and returns a promise that will be resolved with an access token
 * See https://mam-confluence.1and1.com/display/authentication/RefreshTokenService#RefreshTokenService-Createtokenbycode(/token) for details
 * @param authCode
 * @param settings
 * @param pkceVerifier
 */
export async function getTokenByAuthCode(authCode: string, settings: AuthLibSettings, pkceVerifier: string): Promise<AccessTokenPayload> {
  const isStandalone = settings.mode === 'standalone';
  const redirectUri = typeof settings.redirectUri === 'string' ? encodeURIComponent(settings.redirectUri) : getRedirectUri(isStandalone);
  const headers = new Headers();
  headers.append('Authorization', `Basic ${getCredentials(settings.clientId, settings.clientSecret)}`);
  headers.append('Content-Type', 'application/x-www-form-urlencoded');

  const requestInit: RequestInit = {
    method: 'POST',
    headers,
    body: `grant_type=authorization_code&deviceNameB64=${getDeviceName(settings.deviceName)}&redirect_uri=${redirectUri}&userAgentB64=${getUserAgent()}&code=${authCode}&code_verifier=${pkceVerifier}`
  };

  const response = await fetch(`${settings.oauthEndpointToken}`, requestInit);
  if (response.status >= 400) {
    throw new Error(`Token request failed: ${response.status} ${response.statusText}`);
  }

  let accessTokenPayload: AccessTokenPayload;
  try {
    accessTokenPayload = await response.json();
  } catch (ex) {
    throw new Error(`Could not parse token response: ${ex.message} ${ex.stack}`);
  }

  if (!accessTokenPayload.access_token) {
    throw new Error(`Unexpected token data ${JSON.stringify(accessTokenPayload, null, 2)}`);
  }

  return accessTokenPayload;
}

/**
 * Returns the URL for the authorization request
 * @param settings
 * @param state
 * @param pkceChallenge
 */
export function getAuthorizeUrl(settings: AuthLibSettings, state: string, pkceChallenge: string): string {
  const isStandalone = settings.mode === 'standalone';
  const redirectUri = typeof settings.redirectUri === 'string' ? encodeURIComponent(settings.redirectUri) : getRedirectUri(isStandalone);
  let url = `${settings.oauthEndpointAuthorize}?response_type=code&state=${state}&client_id=${settings.clientId}&redirect_uri=${redirectUri}`;
  url = `${url}&code_challenge_method=${PKCE_METHOD}&code_challenge=${pkceChallenge}`

  if (settings.scope) {
    url = `${url}&scope=${settings.scope}`;
  }

  return url;
}

/**
 * Returns a promise that will be resolved with an object containing an auth code and the state value
 * See https://mam-confluence.1and1.com/display/authentication/RefreshTokenService#RefreshTokenService-StartgettingauthorizationonbehalfofaresourceOwner(/authorize) for details
 * @param settings
 * @param pkceChallenge
 */
export async function fetchAuthCode(settings: AuthLibSettings, pkceChallenge: string): Promise<AuthCodePayload> {
  const state = getState(settings.navsid, requestCount++);
  const url = getAuthorizeUrl(settings, state, pkceChallenge);

  let tokenData: AuthCodePayload;
  try {
    tokenData = await getAuthCode(url, settings.authCodeTimeout);
  } catch (err) {
    throw new Error(`Cannot get authCode: ${err.message}`);
  }

  if (!tokenData.state || tokenData.state !== state) {
    throw new Error('state from response does not match given state');
  }

  return tokenData;
}

/**
 * Returns the URI of the login page. After logging in, the user will be redirected to the configured redirectUri
 * @param settings
 * @param pkceChallenge
 */
export function getLoginFormUri(settings: AuthLibSettings, pkceChallenge: string): string {
  const state = getState(settings.navsid, requestCount++);
  return getAuthorizeUrl(settings, state, pkceChallenge);
}
